// TOM-673: alarms opens on the boat’s watch overview, with live readings and
// thresholds visible. Active incidents precede the groups; explicit Active and
// History links keep their focused views. The shell owns the shared snapshot,
// units, evidence and URL so every view tells the same story about the boat.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AlarmsPausedBanner } from '../components/alarms/AlarmsPausedBanner';
import { VesselTypePromptCard } from '../components/alarms/VesselTypePromptCard';
import { ActiveScreen } from '../components/alarms/redesign/ActiveScreen';
import { AddAlarmPicker } from '../components/alarms/redesign/AddAlarmPicker';
import { AlarmOverview } from '../components/alarms/redesign/AlarmOverview';
import { MateAlarmIcon } from '../components/alarms/redesign/MateProposalCard';
import { AllAlarmsScreen } from '../components/alarms/redesign/AllAlarmsScreen';
import { HistoryScreen } from '../components/alarms/redesign/HistoryScreen';
import { MateBar } from '../components/alarms/redesign/MateBar';
import { useBoatChrome } from '../components/app/boatChrome';
import {
  ClockCounterClockwiseIcon,
  CloseIcon,
  ListChecksIcon,
  SearchIcon,
  WarningFillIcon,
} from '../components/app/icons';
import {
  activeAlarmViews,
  alarmSystemGroups,
  configuredAlarmViews,
  customSetupOptions,
  describeBoatSync,
  displayedTitle,
  describeContentDivergence,
  formatModeLabel,
  resolvedPathFor,
  type AlarmDefView,
  type AlarmSeverity,
  type AlarmSystemGroup,
} from '../lib/alarmDisplay';
import { alarmCountsByPath } from '../lib/alarmPicker';
import { alarmDataSource } from '../lib/alarmDataSource';
import { liveValueLines, observationLookup } from '../lib/alarmLive';
import {
  alarmSpecies,
  allClearModel,
  latestReadingMs,
  parseAlarmsView,
  parseEditingId,
  safetyOffTile,
  serializeAlarmsParams,
  watchingTile,
  type AlarmsView,
} from '../lib/alarmRedesign';
import { ALARMS_PAUSED_BAND_EMPTY, isAlarmsPaused } from '../lib/alarmsPaused';
import { getBoatPresence } from '../lib/boatPresence';
import { signalDisplayName, signalLabel, type Signal } from '../lib/signalCatalog';
import { useAlarmEvidence } from '../lib/useAlarmEvidence';
import { useCameraAlarmSnapshots, type CameraAlarmRequest } from '../lib/useCameras';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useSignalCatalog } from '../lib/useSignalCatalog';
import { useUnits } from '../lib/useUnits';

const PRESENCE_TICK_MS = 30_000;

function formatMirroredAt(iso: string | null): string | null {
  if (iso === null) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleString();
}

export function Alarms() {
  const { boatId, boat, alerts, liveSamples, lastSampleAtMs } = useBoatChrome();
  useDocumentTitle(boat ? `${boat.display_name} — Alarms` : 'Alarms — Neomaris');
  // The account's display units (ADR 0189). Read once here for the value lines
  // this page composes; each screen reads it again for the text it composes
  // itself, so there is no half-converted row assembled from two sources.
  const units = useUnits();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), PRESENCE_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const presence = boat
    ? getBoatPresence(boat.last_contacted_at, boat.created_at, lastSampleAtMs, now)
    : 'offline';
  const live = presence === 'live' || presence === 'online';

  const state = alerts.state;
  const response = state.kind === 'ready' ? state.response : null;
  // What kind of boat this is, MIRRORED from the boat's own answer (ADR 0173),
  // or null when nobody has asked it yet. It changes the page's WORDS, never its
  // data: a declared power boat drops "or sail" from the watch vocabulary and
  // the sailing gap notes (its detector can never elect Sailing), while unknown
  // — most of the fleet — keeps today's cautious copy and gets asked once.
  const vesselType = response?.vesselType ?? null;
  // When the owner stopped ALL watching (ADR 0174), mirrored from the boat's own
  // row — null while watching. It drives ONE banner and nothing else: whether a
  // given alarm is evaluating is already in that row's runtime snapshot, which
  // the boat reports as Disabled / `paused_by_owner`, so the screens keep
  // reading the boat rather than a page-level flag that could disagree with it.
  const alarmsPausedAt = response?.alarmsPausedAt ?? null;
  const alarmsPaused = isAlarmsPaused(alarmsPausedAt);
  const boatSyncNote = describeBoatSync(response?.boatSync);
  // A separate line from the sync note, and it can never appear alongside it:
  // divergence is only reported for a boat that IS up to date, which is exactly
  // when `describeBoatSync` falls silent.
  const divergenceNote = describeContentDivergence(response?.boatSync);
  // Live-only, from the boat's own mode_change frame on the alerts lane.
  const currentMode = alerts.currentMode;
  const currentModeLabel = currentMode === null ? null : formatModeLabel(currentMode);
  const mirroredAt = response ? formatMirroredAt(response.mirroredAt) : null;

  // The value line's other half. The catalog carries each path's DECLARED store
  // unit — the unit the compiled set point is also in (ADR 0170) — plus the
  // cloud's last stored sample, which is what a path that hasn't ticked since
  // the socket opened still has to show. One fetch, every screen.
  const catalog = useSignalCatalog(boatId);
  const catalogByPath = useMemo(
    () => new Map<string, Signal>(catalog.signals.map((s) => [s.path, s])),
    [catalog.signals]
  );
  // ONE observation lookup for the page: the value lines above and the inline
  // editor's live proofs below must answer from the same reading, or a row and
  // the panel it expands into would print two different "now"s for one signal.
  const observationFor = useMemo(
    () => observationLookup({ live: liveSamples, catalog: catalogByPath }),
    [liveSamples, catalogByPath]
  );
  const valueLines = useMemo(
    () =>
      response === null
        ? new Map<string, string>()
        : liveValueLines(response.alerts, {
            observationFor,
            unitFor: (path) => catalogByPath.get(path)?.unit ?? liveSamples?.get(path)?.unit,
            units,
            now,
          }),
    [response, liveSamples, catalogByPath, observationFor, units, now]
  );

  // The same catalog names and units the configured band is built with, so an
  // alarm's prefilled title reads the same in both bands (TOM-860).
  const viewOptions = useMemo(
    () => ({
      labelFor: (path: string) => {
        const signal = catalogByPath.get(path);
        return signal === undefined ? signalLabel(path) : signalDisplayName(signal);
      },
      units,
      unitFor: (path: string) => catalogByPath.get(path)?.unit,
    }),
    [units, catalogByPath]
  );
  const activeViews = useMemo(
    () => (response ? activeAlarmViews(response, viewOptions) : []),
    [response, viewOptions]
  );

  // The configured band's rows. Built with the account's units AND the catalog's
  // declared unit per path, because a row states its thresholds beside the live
  // value line above — a recipe row's "min 8.2 ft" and a custom row's sentence
  // both have to be in the unit that line is already in. `unitFor` degrades to
  // "no unit known" while the catalog is still fetching, which is the rendering
  // this page has always had.
  const configuredViews = useMemo(
    () => (response ? configuredAlarmViews(response, viewOptions) : []),
    [response, viewOptions]
  );

  // Which part of the boat each definition watches — the ALL screen's filing.
  // Derived here rather than in `toConfiguredAlarmView` for the same reason as
  // the value lines: it needs the catalog (a custom alarm on a switch channel is
  // filed by the label the installer gave that channel), and a row view must
  // stay renderable with nothing fetched.
  const groups = useMemo(
    () =>
      response === null
        ? new Map<string, AlarmSystemGroup>()
        : alarmSystemGroups(response.alerts, {
            channelLabelFor: (path) => catalogByPath.get(path)?.channelLabel,
          }),
    [response, catalogByPath]
  );

  // The ids the ACTIVE screen is showing — an ALL row's ACTIVE pill means "this
  // one is in the band above", so it is read from that band, not re-derived.
  const surfacedIds = useMemo(() => new Set(activeViews.map((v) => v.id)), [activeViews]);
  // The surfaced rows' transition stamps, for the ALL screen's binary trip age
  // — read from the same views the ACTIVE hero reads, never re-derived.
  const trippedAtById = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of activeViews) {
      if (v.lastTransitionAt !== undefined) map.set(v.id, v.lastTransitionAt);
    }
    return map;
  }, [activeViews]);

  // What the pause confirm is allowed to claim about what is sounding. The
  // active band being EMPTY has two very different causes: the boat told us and
  // nothing is sounding, or the cloud has never mirrored a runtime row for this
  // boat and has no idea. Only the first is "nothing is sounding" — so the count
  // is `null` (unknown) until at least one row carries a runtime snapshot, and
  // the confirm (and the all-clear card) say they cannot tell rather than
  // implying a quiet boat.
  const activeCount = useMemo<number | null>(() => {
    if (response === null) return null;
    return response.alerts.some((row) => row.runtime !== null) ? activeViews.length : null;
  }, [response, activeViews]);

  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const searchInput = useRef<HTMLInputElement>(null);
  const searchButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
  }, [searchOpen]);
  const closeSearch = () => {
    setSearchOpen(false);
    setQuery('');
    searchButton.current?.focus();
  };

  // The stored definitions, by id. The row views carry no trigger, path or
  // custom block — every one of the redesign's new cells (species, threshold
  // line, mini-bar tick, hero caption) needs them, and handing the definitions
  // down beats widening three view types.
  const definitions = useMemo(() => {
    const map = new Map<string, AlarmDefView>();
    if (response) for (const row of response.alerts) map.set(row.definition.id, row.definition);
    return map;
  }, [response]);

  const countsByPath = useMemo(() => alarmCountsByPath(definitions.values()), [definitions]);

  // id → title / severity lookups so HISTORY can name each alarm and colour its
  // marker. A RETIRED definition is absent from both — its history rows are
  // real and stay, wearing a neutral dot and no title (data-mapping trap 4).
  const titleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const [id, def] of definitions)
      map.set(id, displayedTitle(def, customSetupOptions(def, viewOptions)));
    return map;
  }, [definitions, viewOptions]);
  const severityById = useMemo(() => {
    const map = new Map<string, AlarmSeverity>();
    for (const [id, def] of definitions) if (def.severity !== null) map.set(id, def.severity);
    return map;
  }, [definitions]);
  // id → the STORE unit of that alarm's own signal (ADR 0170). A history row
  // prints the single value the boat stamped on a transition, and that value is
  // in the same domain as the compiled set point — so it is the catalog's unit
  // for the alarm's resolved path, or nothing at all. A bare number wearing a
  // guessed unit would be worse than a bare number.
  const unitById = useMemo(() => {
    const map = new Map<string, string>();
    for (const [id, def] of definitions) {
      const path = resolvedPathFor(def);
      const unit = path === null ? null : (catalogByPath.get(path)?.unit ?? null);
      if (unit !== null && unit.trim().length > 0) map.set(id, unit);
    }
    return map;
  }, [definitions, catalogByPath]);
  // id → the PATH that unit belongs to. The unit says what a number is written
  // in; only the path says what it measures, and that is what decides whether
  // the account may restate it (ADR 0189) — hPa is a barometer or an engine's
  // oil pressure, and only one of the two ever converts.
  const pathById = useMemo(() => {
    const map = new Map<string, string>();
    for (const [id, def] of definitions) {
      const path = resolvedPathFor(def);
      if (path !== null && path.trim().length > 0) map.set(id, path);
    }
    return map;
  }, [definitions]);

  // The selected screen and the one open inline editor live in the URL so a
  // pasted link and a snapshot refresh both preserve the owner's place.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseAlarmsView(searchParams.get('view'));
  const editingId = parseEditingId(searchParams.get('editing'));

  // TOM-673: state filters have been removed. Old bookmarks must show every
  // configured alarm, including the row whose editor they link to.
  useEffect(() => {
    if (!searchParams.has('filter')) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('filter');
        return params;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const setScreen = useCallback(
    (next: { view: AlarmsView; editing?: string | null }) => {
      setSearchParams(
        (prev) => {
          // Merge, never replace: other params on this URL belong to someone
          // else, and a screen switch must not drop them.
          const params = new URLSearchParams(prev);
          const serialized = serializeAlarmsParams({ ...next, filter: 'all' });
          for (const [key, value] of Object.entries(serialized)) {
            if (value === null) params.delete(key);
            else params.set(key, value);
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const goToAll = useCallback(() => setScreen({ view: 'all' }), [setScreen]);
  // Open / close the one inline editor. Closing is the same call with `null`,
  // and `serializeAlarmsParams` deletes the param either way.
  const setEditingId = useCallback(
    (next: string | null) => setScreen({ view: 'all', editing: next }),
    [setScreen]
  );
  const editOnAllScreen = useCallback(
    (id: string) => setScreen({ view: 'all', editing: id }),
    [setScreen]
  );

  // The newest reading anywhere aboard — the live socket's last sample OR the
  // catalog's stored stamps, whichever is newer. A boat that has not spoken
  // since the page opened has only the second, and reading the live half alone
  // made an eleven-day-old-but-real history print "No readings yet".
  const latestReading = useMemo(
    () => latestReadingMs({ lastSampleAtMs, catalogSignals: catalog.signals }),
    [lastSampleAtMs, catalog.signals]
  );

  // The footer tiles' models. "Watching" is a claim about what the boat is
  // EVALUATING, so it excludes the pending and the runtime-disabled; the
  // safety-off tile names the alarms an owner switched off, which is a
  // different sentence from "not evaluating" and stays separate from it.
  const watching = useMemo(
    () => watchingTile(configuredViews, surfacedIds, latestReading, now),
    [configuredViews, surfacedIds, latestReading, now]
  );
  const safetyOff = useMemo(
    () => safetyOffTile(configuredViews, groups),
    [configuredViews, groups]
  );
  const allClear = useMemo(
    () =>
      allClearModel({
        alarmsPaused,
        activeCount,
        watching: watching.count,
        readingAgoText: watching.readingAgoText,
        pausedText: ALARMS_PAUSED_BAND_EMPTY,
      }),
    [alarmsPaused, activeCount, watching]
  );

  // EVIDENCE. Analog rows get a 6 h series for their sparkline and mini-bar;
  // surfaced BINARY rows get their own firing history for the 24 h tick strip
  // (a numeric range read would say nothing about a float switch).
  // Enabled rows use the compiled trigger; off rows preview their source so
  // their sensor data stays visible without enabling evaluation.
  const analogPaths = useMemo(() => {
    const paths: string[] = [];
    const catalogPaths = new Set(catalogByPath.keys());
    for (const def of definitions.values()) {
      const source = alarmDataSource(def, catalogPaths);
      if (alarmSpecies(source) !== 'analog') continue;
      const path = resolvedPathFor(source);
      if (path !== null && path.length > 0) paths.push(path);
    }
    return paths;
  }, [definitions, catalogByPath]);
  const binaryAlertIds = useMemo(
    () =>
      activeViews
        .filter((v) => {
          const def = definitions.get(v.id);
          return def !== undefined && alarmSpecies(def) === 'binary';
        })
        .map((v) => v.id),
    [activeViews, definitions]
  );
  const evidence = useAlarmEvidence({
    boatId,
    analogPaths,
    binaryAlertIds,
    // Evidence is only ever read beside a snapshot, so it refreshes with one.
    refreshKey: response?.mirroredAt ?? '',
  });

  // A camera alarm's snapshot beside its evidence (TOM-797), read once per alarm.
  const cameraAlarmRequests = useMemo(() => {
    const out: CameraAlarmRequest[] = [];
    for (const v of activeViews) {
      const def = definitions.get(v.id);
      const path = def === undefined ? null : resolvedPathFor(def);
      if (path !== null) out.push({ alarmId: v.id, path, at: v.lastTransitionAt });
    }
    return out;
  }, [activeViews, definitions]);
  const cameraSnapshots = useCameraAlarmSnapshots(boatId, cameraAlarmRequests);

  const segments: { key: AlarmsView; label: string; icon: ReactNode }[] = [
    { key: 'all', label: 'All alarms', icon: <ListChecksIcon /> },
    { key: 'active', label: 'Active', icon: <WarningFillIcon /> },
    { key: 'history', label: 'History', icon: <ClockCounterClockwiseIcon /> },
  ];

  return (
    <section className="al-page" data-testid="alarms">
      <header className="al-header">
        <div className="page-head">
          <h1>
            Alarms <span className="al-page__boat">{boat?.display_name ?? 'Your boat'}</span>
          </h1>
          {state.kind === 'ready' ? (
            <p
              className="al-report-status"
              id="alarm-report-status"
              data-testid={!live ? 'alarms-stale-banner' : undefined}
            >
              {!live
                ? mirroredAt
                  ? `Offline · Alarm state from ${mirroredAt}`
                  : 'Offline · Showing the last alarm state received'
                : watching.readingAgoText === null
                  ? 'No readings yet'
                  : `Latest reading ${watching.readingAgoText.toLowerCase()}`}
            </p>
          ) : null}
        </div>
        {state.kind === 'ready' ? (
          <AlarmOverview
            activeCount={activeCount}
            onShowActive={() => setScreen({ view: 'active' })}
            reportDescriptionId="alarm-report-status"
          />
        ) : null}
      </header>

      {state.kind === 'loading' ? <p className="muted">Loading alarms…</p> : null}
      {state.kind === 'error' ? (
        <ul className="form-errors">
          <li>{state.message}</li>
        </ul>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {/* PAUSED: first on the page, above everything, because "nothing is
              being watched" changes how every screen below it must be read. The
              same control lives at the FOOT of the ALL ALARMS screen while the
              boat is watching — pausing is a rare deliberate act and gets a
              whisper, not a banner. Both are the one component, placed by
              state. */}
          {alarmsPaused ? (
            <AlarmsPausedBanner
              boatId={boatId}
              pausedAt={alarmsPausedAt}
              boatOnline={live}
              activeCount={activeCount}
              onSent={alerts.refresh}
            />
          ) : null}
          {/* Asked once, of a boat that has never been asked and is online to
              hear the answer. It renders ABOVE the screens because it changes
              how they read — and it disappears when the boat's own echo lands,
              not when the button is clicked. */}
          <VesselTypePromptCard
            boatId={boatId}
            vesselType={vesselType}
            boatOnline={live}
            onSent={alerts.refresh}
          />
          {/* Which alarms are armed depends on what the boat is doing, so the
              page says what that is — but ONLY once the boat has told us. No
              mode_change frame yet ⇒ nothing here. A guessed mode would pause
              rows on a schedule the boat never elected. Beside it, whether the
              definitions saved in the cloud have actually reached the boat:
              every row below is a claim about the boat, and this is the sentence
              that says whether the boat has heard any of them. */}
          {currentModeLabel !== null || boatSyncNote !== null || divergenceNote !== null ? (
            <div className="al-notes">
              {currentModeLabel !== null ? (
                <span className="alarm-mode-chip" data-testid="alarms-current-mode">
                  Boat is: {currentModeLabel}
                </span>
              ) : null}
              {boatSyncNote !== null ? (
                <p className="alarm-boatsync" data-testid="alarm-boatsync">
                  {boatSyncNote}
                </p>
              ) : null}
              {divergenceNote !== null ? (
                <p className="alarm-boatsync" data-testid="alarm-divergence">
                  {divergenceNote}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="al-navigation">
            <nav className="al-segments" aria-label="Alarm views">
              {segments.map((segment) => (
                <button
                  key={segment.key}
                  type="button"
                  className={`al-segment${view === segment.key ? ' al-segment--selected' : ''}`}
                  aria-label={segment.label}
                  aria-current={view === segment.key ? 'page' : undefined}
                  onClick={() => {
                    setScreen({ view: segment.key });
                    setSearchOpen(false);
                    setQuery('');
                  }}
                >
                  {segment.icon}
                  {segment.key === 'all' ? (
                    <>
                      <span className="al-segment__full">{segment.label}</span>
                      <span className="al-segment__short" aria-hidden="true">
                        All
                      </span>
                    </>
                  ) : (
                    segment.label
                  )}
                </button>
              ))}
            </nav>
            <div className="al-page-actions">
              <button
                ref={searchButton}
                type="button"
                className="al-icon-button"
                aria-label="Search alarms"
                title="Search alarms"
                aria-expanded={searchOpen}
                aria-controls="alarms-search-panel"
                data-testid="alarms-search-toggle"
                onClick={() => {
                  if (searchOpen) closeSearch();
                  else {
                    setScreen({ view: 'all', editing: editingId });
                    setSearchOpen(true);
                  }
                }}
              >
                <SearchIcon />
              </button>
              <button
                type="button"
                className="al-icon-button al-icon-button--primary"
                aria-label="Add alarm"
                title="Add alarm"
                aria-expanded={pickerOpen}
                aria-controls="alarms-add-panel"
                data-testid="alarms-add-btn"
                onClick={() => setPickerOpen((open) => !open)}
              >
                <span className="al-icon-button__plus" aria-hidden="true">
                  +
                </span>
              </button>
            </div>
          </div>

          {searchOpen ? (
            <div className="al-search-panel" id="alarms-search-panel">
              <SearchIcon />
              <input
                ref={searchInput}
                type="search"
                className="al-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') closeSearch();
                }}
                placeholder="Name or system"
                aria-label="Search alarms"
                data-testid="alarms-search"
              />
              <button
                type="button"
                className="al-icon-button"
                aria-label="Close search"
                onClick={closeSearch}
              >
                <CloseIcon />
              </button>
            </div>
          ) : null}

          {pickerOpen ? (
            <section className="al-create" id="alarms-add-panel" aria-label="New alarm">
              <details className="al-compose">
                <summary>
                  <MateAlarmIcon /> Describe an alarm to Neo
                </summary>
                <MateBar
                  boatId={boatId}
                  onProposals={() => setScreen({ view: 'all' })}
                  onChange={alerts.refresh}
                />
              </details>
              <AddAlarmPicker
                boatId={boatId}
                catalogByPath={catalogByPath}
                alarmCounts={countsByPath}
                vesselType={vesselType}
                now={now}
                onClose={() => setPickerOpen(false)}
                onCreated={alerts.refresh}
              />
            </section>
          ) : null}

          {view === 'active' || (view === 'all' && activeViews.length > 0) ? (
            <ActiveScreen
              showSummary={view === 'active'}
              boatId={boatId}
              views={activeViews}
              definitions={definitions}
              catalogByPath={catalogByPath}
              valueLines={valueLines}
              evidence={evidence}
              cameraSnapshots={cameraSnapshots}
              boatOffline={!live}
              alarmsPaused={alarmsPaused}
              allClear={allClear}
              watching={watching}
              safetyOff={safetyOff}
              boatSyncNote={boatSyncNote}
              onActionComplete={alerts.refresh}
              onNavigateToFilter={goToAll}
              onEditAlarm={editOnAllScreen}
              now={now}
            />
          ) : null}
          {view === 'all' ? (
            <AllAlarmsScreen
              boatId={boatId}
              views={configuredViews}
              definitions={definitions}
              catalogByPath={catalogByPath}
              groups={groups}
              surfacedIds={surfacedIds}
              trippedAtById={trippedAtById}
              valueLines={valueLines}
              observationFor={observationFor}
              evidence={evidence}
              query={query}
              currentMode={currentMode}
              vesselType={vesselType}
              boatOnline={live}
              activeCount={activeCount}
              alarmsPausedAt={alarmsPausedAt}
              alarmsPaused={alarmsPaused}
              boatSyncNote={boatSyncNote}
              editingId={editingId}
              onEditingChange={setEditingId}
              onChange={alerts.refresh}
              onPauseSent={alerts.refresh}
              now={now}
            />
          ) : null}
          {view === 'history' ? (
            <HistoryScreen
              boatId={boatId}
              response={state.response}
              titleById={titleById}
              severityById={severityById}
              unitById={unitById}
              pathById={pathById}
              now={now}
            />
          ) : null}
        </>
      ) : null}
    </section>
  );
}
